import equals from 'react-fast-compare';
import { type Primitive } from 'type-fest';
import { type ObjectEntries } from 'type-fest/source/entries';

/**
 * Extracts a subset of properties from an object.
 * @param object The original object from which to extract properties.
 * @param keys An array of keys to extract from the original object.
 * @returns A new object containing only the properties specified in the keys array.
 * @example
 * pick({a: 1, b: 2, c: 3}, ['a', 'c']); // {a: 1, c: 3}
 */
export const pick = <T extends Record<string, any>, TKeys extends keyof T>(
  object: T,
  keys: readonly TKeys[],
): Pick<T, TKeys> => {
  const result: any = {};
  for (const key of keys) {
    if (object[key]) {
      result[key] = object[key];
    }
  }
  return result;
};

/**
 * Creates a new object by omitting properties of an existing object.
 * @param object The original object from which to omit properties.
 * @param keys An array of keys to omit from the original object.
 * @returns A new object with the same properties as the original object except
 *   for the properties specified in the keys array.
 * @example
 * omit({a: 1, b: 2, c: 3}, ['b']); // {a: 1, c: 3}
 */
export const omit = <T extends Record<string, any>, TKeys extends keyof T>(
  object: T,
  keys: readonly TKeys[],
): Omit<T, TKeys> => {
  const result: any = {};
  for (const key of Object.keys(object)) {
    if (!keys.includes(key as any)) {
      result[key] = object[key];
    }
  }
  return result;
};

/**
 * Does a shallow comparison of two objects.
 * @param a
 * @param b
 * @returns true if the objects have the same keys and values, false otherwise.
 * @example
 * shallowEqual({ a: 1, b: 2 }, { a: 1, b: 2 }) // true
 * shallowEqual({ a: 1, b: 2 }, { a: 1, b: 3 }) // false
 * shallowEqual({ a: 1, b: 2 }, { a: 1 }) // false
 * shallowEqual({ a: 1, b: 2 }, { a: 1, b: 2, c: 3 }) // false
 * shallowEqual([1, 2, 3], [1, 2, 3]) // true
 */
export const shallowEqual = (
  a?: Record<string | number, Primitive> | null,
  b?: Record<string | number, Primitive> | null,
) => {
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') {
    return a === b;
  }
  const keysA = Object.keys(a);
  return keysA.length === Object.keys(b).length && keysA.every((key) => a[key] === b[key]);
};

export const deepEqual = equals;

/**
 * Merges two objects recursively, creating a new object with the properties of both objects.
 * @param originalObject The object to be merged into.
 * @param overrideObject The object to merge into the original object.
 * @returns A new object with the properties of both input objects.
 * @example
 * const currentState = {a: {b: 1}};
 * const newState = {a: {c: 2}};
 * deepMerge(currentState, newState); // {a: {b: 1, c: 2}}
 */
export const deepMerge = <TOriginal extends Record<any, any>, TOverride extends Record<any, any>>(
  originalObject: TOriginal,
  overrideObject: TOverride,
): TOriginal & TOverride => {
  const merged = { ...originalObject } as TOriginal & TOverride;

  for (const key of Object.keys(overrideObject) as Array<keyof TOverride>) {
    if (merged[key] && typeof merged[key] === 'object' && !Array.isArray(merged[key])) {
      merged[key] = deepMerge(merged[key], overrideObject[key]);
    } else {
      merged[key] = overrideObject[key];
    }
  }

  return merged;
};

/**
 * Transforms the object by applying a mapper function to each key-value pair.
 * @param object The original object to be transformed.
 * @param mapper The mapper function(takes a key-value pair and must return a new key-value pair).
 * @returns A new object with the transformed properties.
 * @example
 * objectMap({a: 1, b: 2}, ([key, value]) => [key.toUpperCase(), value*2]) // {A: 2, B: 4}
 */
export const objectMap = <TKey extends string, TValue, TResultKey extends string, TResultValue>(
  object: Record<TKey, TValue>,
  mapper: ([key, value]: [TKey, TValue], index: number) => readonly [TResultKey, TResultValue],
): Record<TResultKey, TResultValue> =>
  Object.fromEntries(
    (Object.entries(object) as ObjectEntries<typeof object>).map(mapper),
  ) as Record<TResultKey, TResultValue>;

export const filterMap = <TKey extends string, TValue, TResultKey extends string, TResultValue>(
  object: Record<TKey, TValue>,
  mapper: (
    [key, value]: [TKey, TValue],
    index: number,
  ) => readonly [TResultKey, TResultValue] | null | undefined | false,
) =>
  Object.fromEntries(
    (Object.entries(object) as ObjectEntries<typeof object>).map(mapper).filter(Boolean),
  ) as any as Record<string, TResultValue>;

type Compacted<T> = {
  [TKey in keyof T as T[TKey] extends undefined | null ? never : TKey]: T[TKey];
};

/**
 * Removes undefined and null values from an object, similar to ruby's `compact`.
 * @param object The object to compact.
 * @returns A new object with the same properties as the original object except
 *   for the properties that are undefined or null.
 * @example
 * compact({a: 1, b: undefined, c: null}); // {a: 1}
 */
export const compact = <T extends object>(object: T) =>
  Object.fromEntries(
    Object.entries(object).filter(([, value]) => value !== undefined && value !== null),
  ) as Compacted<T>;

/**
 * Creates an array based on a object string path.
 * @param path The string path.
 * @returns The resulting array.
 * @example
 * toPath("object.nested.option[0].child"); // ["object", "nested", "option", 0, "child"]
 */
export const toPath = (path: string) =>
  path
    .split(/[[\].]/g)
    .filter(Boolean)
    .map((segment) => {
      const parsedSegment = Number(segment);
      return isNaN(parsedSegment) ? segment : parsedSegment;
    });

/**
 * Get the value of a property in a nested object.
 * @param object The object to look for.
 * @param path The string path.
 * @returns The property value.
 * @example
 * toPath({ a: { b: { c: 1 } } }, "a.b.c"); // 1
 */
export const getIn = <T>(obj: Record<string, any>, path: string): T | undefined => {
  let current = obj;

  for (const key of toPath(path)) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (current === undefined) {
      return undefined;
    }
    current = current[key];
  }

  return current as T;
};

export const keys = Object.keys as <T extends {}>(obj: T) => Array<keyof T>;

export const entries = Object.entries as <T extends {}>(obj: T) => ObjectEntries<T>;

/**
 * Determines if the provided value is one of the keys in the object.
 * @param value - The value to check.
 * @param obj - The object to check against.
 * @returns `true` if the value is one of the keys in the object, `false` otherwise.
 * @example
 * isKeyOf('a', {a: 1, b: 2, c: 3}) // true
 * isKeyOf('d', {a: 1, b: 2, c: 3}) // false
 */
export const isKeyOf = <const T extends { [key: string]: unknown }>(
  value: string | number | symbol,
  obj: T,
): value is keyof T => value in obj;
